import * as THREE from 'three';
import type { CollisionWorld } from '@arena/shared';

const MIN_DISTANCE = 2;
const MAX_DISTANCE = 22;
const MIN_PITCH = -1.2;
const MAX_PITCH = 1.35;
const PIVOT_HEIGHT = 0.9;
/** How far in front of a wall the camera stops. */
const WALL_MARGIN = 0.25;

/** WoW-style orbit camera around the player, pulled in by walls. */
export class CameraRig {
  yaw = 0;
  pitch = 0.3;
  distance = 9;
  private smoothed = 9;

  constructor(readonly camera: THREE.PerspectiveCamera) {}

  rotate(dx: number, dy: number): void {
    this.yaw -= dx * 0.005;
    this.pitch = Math.min(
      MAX_PITCH,
      Math.max(MIN_PITCH, this.pitch + dy * 0.005),
    );
  }

  zoom(delta: number): void {
    this.distance = Math.min(
      MAX_DISTANCE,
      Math.max(MIN_DISTANCE, this.distance * Math.exp(delta * 0.001)),
    );
  }

  update(target: THREE.Vector3, world: CollisionWorld, dt: number): void {
    const pivot = { x: target.x, y: target.y + PIVOT_HEIGHT, z: target.z };
    const dir = {
      x: Math.sin(this.yaw) * Math.cos(this.pitch),
      y: Math.sin(this.pitch),
      z: Math.cos(this.yaw) * Math.cos(this.pitch),
    };
    const hit = world.castRay(pivot, dir, this.distance);
    const allowed = hit
      ? Math.max(0.5, hit.distance - WALL_MARGIN)
      : this.distance;
    // Snap in toward walls immediately, ease back out.
    this.smoothed =
      allowed < this.smoothed
        ? allowed
        : this.smoothed + (allowed - this.smoothed) * Math.min(1, dt * 6);
    this.camera.position.set(
      pivot.x + dir.x * this.smoothed,
      pivot.y + dir.y * this.smoothed,
      pivot.z + dir.z * this.smoothed,
    );
    this.camera.lookAt(pivot.x, pivot.y, pivot.z);
  }
}
